import {
  FETCH_USER,
  FETCH_USER_SUCCESS,
  FETCH_USER_FAILED,
  USER_SIGN_IN,
  USER_SIGN_IN_FAILED,
  USER_SIGN_OUT,
  CLEAR_LOGIN_ERROR,
  REQUEST_OTP,
  REQUEST_OTP_SUCCESS,
  REQUEST_OTP_FAILED,
  UPDATE_USER_WALLET_HISTORY,
  SEND_RESET_EMAIL,
  SEND_RESET_EMAIL_SUCCESS,
  SEND_RESET_EMAIL_FAILED
} from "../store/types";

import store from '../store/store';
import { firebase } from '../config/configureFirebase';
import { onValue, update, set, off, get } from "firebase/database";
import { onAuthStateChanged, signInWithCredential, signInWithPopup, signOut, sendPasswordResetEmail, signInWithEmailAndPassword, signInWithCustomToken } from "firebase/auth";
import { uploadBytesResumable, getDownloadURL } from "firebase/storage";
import base64 from 'react-native-base64';
import AccessKey from '../other/AccessKey.js';
import SessionCache from '../other/SessionCache.js';

const createUserData = async (user, dispatch) => {
  console.log("createUserData: Starting profile creation process");
  const { config } = firebase;

  const applyDisplayName = (displayName, target) => {
    if (!displayName || !target) return;
    const parts = String(displayName).trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return;
    if (!target.firstName) target.firstName = parts[0];
    if (!target.lastName && parts.length > 1) target.lastName = parts.slice(1).join(" ");
  };
  
  let data = {
    uid : user.uid,
    mobile: user.phoneNumber || '',
    email: user.email || '',
    firstName: '',
    lastName: '',
    verifyId: ''
  }

  applyDisplayName(user.displayName, data);
  if (user.photoURL) {
    data['profile_image'] = user.photoURL;
  }

  console.log("createUserData: User provider data length:", user.providerData.length);
  if(user.providerData.length == 0 && user.email){
    console.log("createUserData: Setting email from user.email:", user.email);
    data.email = user.email;
  }
  if(user.providerData.length > 0 && user.phoneNumber){
    console.log("createUserData: Setting mobile from user.phoneNumber:", user.phoneNumber);
    data.mobile = user.phoneNumber;
  }
  if (user.providerData.length > 0) {
    const provideData = user.providerData[0] || {};
    console.log("createUserData: Provider data:", provideData.providerId);
    if (provideData.providerId === 'phone') {
      data.mobile = provideData.phoneNumber;
    }
    if (provideData.providerId == "google.com" || provideData.providerId == 'apple.com') {
      if (provideData.email) {
        data.email = provideData.email;
      }
      if (provideData.phoneNumber) {
        data.mobile = provideData.phoneNumber;
      }
      applyDisplayName(provideData.displayName, data);
      if (provideData.photoURL) {
        data['profile_image'] = provideData.photoURL;
      }
    } else {
      // Fallback for other providers: keep profile data complete for booking/payment flows.
      if (!data.email && provideData.email) {
        data.email = provideData.email;
      }
      if (!data.mobile && provideData.phoneNumber) {
        data.mobile = provideData.phoneNumber;
      }
      applyDisplayName(provideData.displayName, data);
      if (!data.profile_image && provideData.photoURL) {
        data['profile_image'] = provideData.photoURL;
      }
    }
  }
  if(user.providerData.length > 0 && user.verifyId){
    data.verifyId = user.verifyId;
  }

  // Check if language data is available before proceeding
  const langState = store.getState().languagedata;
  if (!langState || !langState.langlist || !langState.defaultLanguage) {
    console.log("createUserData: Language data not ready, dispatching FETCH_USER_FAILED");
    dispatch({
      type: FETCH_USER_FAILED,
      payload: { code: "Language not ready", message: "Language data not available" }
    });
    return;
  }

  const settings = store.getState().settingsdata.settings;
  // Prefer the current origin so requests stay same-origin (avoid CORS).
  // Fallback to web.app if origin is not available.
  const currentOrigin = (typeof window !== 'undefined' && window.location && window.location.origin) ? window.location.origin : null;
  let host = currentOrigin || `https://${config.projectId}.web.app`;
  let url = `${host}/check_auth_exists`;
  
  console.log("createUserData: Making HTTP request to:", url);
  console.log("createUserData: Request payload data:", JSON.stringify(data));
  
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        "Authorization": "Basic " + base64.encode(config.projectId + ":" + AccessKey)
      },
      body: JSON.stringify({data: JSON.stringify(data)})
    });
    
    console.log("createUserData: HTTP response status:", response.status);
    const json = await response.json();
    console.log("createUserData: HTTP response data:", json);
    
    if(json.uid){
      console.log("createUserData: User profile created/found, dispatching FETCH_USER_SUCCESS");
      
      // Sauvegarder dans le cache pour persistance à long terme
      await SessionCache.saveUserProfile(json);
      
      dispatch({
        type: FETCH_USER_SUCCESS,
        payload: json
      });
    } else{
      console.log("createUserData: Error in user creation/verification:", json.error);
      dispatch({
        type: FETCH_USER_FAILED,
        payload: { code: langState.defaultLanguage.auth_error, message: json.error }
      });
    }
  } catch (error) {
    console.log("createUserData: HTTP request failed:", error);
    dispatch({
      type: FETCH_USER_FAILED,
      payload: { code: langState.defaultLanguage.auth_error, message: error.message }
    });
  }
};

export const fetchUser = () => async (dispatch) => {
  const {
    auth,
    config,
    singleUserRef
  } = firebase;

  // Check if database is available
  if (!singleUserRef) {
    console.warn("Database not available, cannot fetch user data");
    dispatch({
      type: FETCH_USER_FAILED,
      payload: "Database not available"
    });
    return;
  }

  dispatch({
    type: FETCH_USER,
    payload: null
  });
  
  // Vérifier d'abord le cache pour une reconnexion rapide
  console.log("fetchUser: Checking cache for existing session...");
  const cachedProfile = await SessionCache.getUserProfile();
  const isSessionValid = await SessionCache.isSessionValid();
  
  if (cachedProfile && isSessionValid) {
    console.log("fetchUser: Valid cached session found, dispatching cached profile");
    dispatch({
      type: FETCH_USER_SUCCESS,
      payload: cachedProfile
    });
    
    // Prolonger la session automatiquement
    await SessionCache.extendSession();
    
    // Continuer quand même avec onAuthStateChanged pour sync en arrière-plan
  }
  
  console.log("fetchUser: Setting up onAuthStateChanged listener...");
  onAuthStateChanged(auth, user => {
    console.log("fetchUser: onAuthStateChanged triggered, user exists:", !!user);
    if (user) {
      console.log("fetchUser: User is logged in, fetching profile for uid:", user.uid);
      console.log("fetchUser: singleUserRef function available:", !!singleUserRef);
      const userRef = singleUserRef(user.uid);
      console.log("fetchUser: userRef created:", !!userRef);
      
      // Add a timeout to handle cases where onValue doesn't trigger
      const timeoutId = setTimeout(() => {
        console.log("fetchUser: onValue timeout reached, forcing profile creation");
        // If onValue doesn't respond in 5 seconds, create profile
        createUserData(user, dispatch);
      }, 5000);
      
      onValue(userRef, async snapshot => {
        clearTimeout(timeoutId); // Clear timeout since onValue triggered
        console.log("fetchUser: onValue callback triggered, snapshot exists:", !!snapshot);
        console.log("fetchUser: snapshot has data:", !!snapshot.val());
        
        if (snapshot.val()) {
          let profile = snapshot.val();
          profile.uid = user.uid;
          console.log("fetchUser: User profile found, dispatching success");
          
          // Sauvegarder dans le cache
          await SessionCache.saveUserProfile(profile);
          
          dispatch({
            type: FETCH_USER_SUCCESS,
            payload: profile
          });
        }else{
          console.log("fetchUser: No profile data, creating new user data via createUserData");
          createUserData(user, dispatch);
        }
      }, (error) => {
        clearTimeout(timeoutId);
        console.log("fetchUser: onValue error:", error);
        // If there's a database error, try to create the profile
        createUserData(user, dispatch);
      });
    } else {
      console.log("fetchUser: No user logged in, dispatching FETCH_USER_FAILED");
      const currentState = store.getState();
      console.log("fetchUser: Current language state:", !!currentState.languagedata);
      console.log("fetchUser: Default language available:", !!currentState.languagedata?.defaultLanguage);
      
      // Use specific message that authreducer can recognize as "not an error"
      dispatch({
        type: FETCH_USER_FAILED,
        payload: "No user logged in"
      });
    }
  });
};

export const validateReferer = async (referralId) => {
  const {
    config
  } = firebase;
  const response = await fetch(`https://${config.projectId}.web.app/validate_referrer`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      referralId: referralId
    })
  })
  const json = await response.json();
  return json;
};

export const checkUserExists = async (data) => {
  const {
    config
  } = firebase;

  const settings = store.getState().settingsdata.settings;
  const currentOrigin = (typeof window !== 'undefined' && window.location && window.location.origin) ? window.location.origin : null;
  let host = currentOrigin || `https://${config.projectId}.web.app` || `http://localhost:3000`;
  let url = `${host}/check_user_exists`;
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      "Authorization": "Basic " + base64.encode(config.projectId + ":" + AccessKey)
    },
    body: JSON.stringify({
      email: data.email,
      mobile: data.mobile
    })
  })
  const json = await response.json();
  return json;
};

export const mainSignUp = async (regData) => {
  const {
    config
  } = firebase;
  let url = `https://${config.projectId}.web.app/user_signup`;
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ regData: regData })
  })
  const res = await response.json();
  return res;
};

export const updateProfileWithEmail = (profileData) => async (dispatch) => {
  const {
    config
  } = firebase;
  try{
    const settings = store.getState().settingsdata.settings;
    let host = window && window.location && settings.CompanyWebsite === window.location.origin? window.location.origin : `https://${config.projectId}.web.app`
    let url = `${host}/update_user_email`;
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        "Authorization": "Basic " + base64.encode(config.projectId + ":" + AccessKey)
      },
      body: JSON.stringify(profileData)
    })
    const result = await response.json();
    if(result.error){ 
      return {success: false, error: result.error}
    }
  }catch(error){
    return {success: false, error: error}
  }
}


export const requestPhoneOtpDevice = (verificationId) => async (dispatch) => {
    dispatch({
      type: REQUEST_OTP_SUCCESS,
      payload: verificationId
    }); 
}

export const mobileSignIn = (verficationId, code) => (dispatch) => {
  const {
    auth,
    mobileAuthCredential,
  } = firebase;

  dispatch({
    type: USER_SIGN_IN,
    payload: null
  });
  signInWithCredential(auth, mobileAuthCredential(verficationId, code))
    .then((user) => {
      //OnAuthStateChange takes care of Navigation
    }).catch(error => {
      dispatch({
        type: USER_SIGN_IN_FAILED,
        payload: error
      });
    });
};

export const saveAddresses = async (uid, location, name) => {
  const { singleUserRef } = firebase;
  onValue(child(singleUserRef(uid), "savedAddresses"), (savedAdd) => {
    if (savedAdd.val()) {
      let addresses = savedAdd.val();
      let didNotMatch = true;
      for (let key in addresses) {
        let entry = addresses[key];
        if (entry.name == name ) {
          didNotMatch = false;
          update(child(singleUserRef(uid),"savedAddresses/" + key),{
            description: location.add,
            lat: location.lat,
            lng: location.lng,
            count: 1,
            name: name
          });
          break;
        }
      }
      if (didNotMatch) {
        push(child(singleUserRef(uid),"savedAddresses"),{
          description: location.add,
          lat: location.lat,
          lng: location.lng,
          count: 1,
          name: name
        });
      }
    } else {
      push(child(singleUserRef(uid),"savedAddresses"),{
        description: location.add,
        lat: location.lat,
        lng: location.lng,
        count: 1,
        name: name
      });
    }
  }, {onlyOnce: true});
};

export const googleLogin = (idToken, accessToken) => (dispatch) => {

  const {
    auth,
    googleCredential
  } = firebase;

  dispatch({
    type: USER_SIGN_IN,
    payload: null
  });

  const credential = googleCredential(idToken, accessToken);
  signInWithCredential(auth, credential)
    .then((user) => {
      //OnAuthStateChange takes care of Navigation
    })
    .catch(error => {
      console.log(error);
      dispatch({
        type: USER_SIGN_IN_FAILED,
        payload: error
      });
    });
}

export const appleSignIn = (credentialData) => (dispatch) => {

  const {
    auth,
    appleProvider
  } = firebase;

  dispatch({
    type: USER_SIGN_IN,
    payload: null
  });
  if (credentialData) {
    const credential = appleProvider.credential(credentialData);
    signInWithCredential(auth, credential)
      .then((user) => {
        //OnAuthStateChange takes care of Navigation
      })
      .catch((error) => {
        dispatch({
          type: USER_SIGN_IN_FAILED,
          payload: error
        });
      });
  } else {
    signInWithPopup(auth, appleProvider).then(function (result) {
      signInWithCredential(auth, result.credential)
        .then((user) => {
        //OnAuthStateChange takes care of Navigation
        })
        .catch(error => {
          dispatch({
            type: USER_SIGN_IN_FAILED,
            payload: error
          });
        }
        );
    }).catch(function (error) {
      dispatch({
        type: USER_SIGN_IN_FAILED,
        payload: error
      });
    });
  }
};

export const signOff = () => async (dispatch) => {

  const {
    auth,
    singleUserRef,
    walletHistoryRef,
    userNotificationsRef
  } = firebase;

  const currentUser = auth.currentUser;
  if (!currentUser) {
    // Already signed out; still clear redux state
    dispatch({ type: USER_SIGN_OUT, payload: null });
    return;
  }

  const uid = currentUser.uid;

  // Safely detach listeners
  try { off(singleUserRef(uid)); } catch (e) {}
  try { off(walletHistoryRef(uid)); } catch (e) {}
  try { off(userNotificationsRef(uid)); } catch (e) {}

  // Nettoyer le cache de session
  console.log("signOut: Clearing session cache...");
  await SessionCache.clearUserSession();

  // Fetch profile once, update if driver, then sign out
  get(singleUserRef(uid))
    .then(snapshot => {
      const profile = snapshot?.val?.() ? snapshot.val() : snapshot.val();
      if (profile && profile.usertype === 'driver') {
        try { update(singleUserRef(uid), { driverActiveStatus: false }); } catch (e) {}
      }
    })
    .finally(() => {
      signOut(auth)
        .then(() => {
          dispatch({ type: USER_SIGN_OUT, payload: null });
        })
        .catch(() => {
          // Even if signOut throws, ensure local state resets
          dispatch({ type: USER_SIGN_OUT, payload: null });
        });
    });
};

export const updateProfile = (updateData) => async (dispatch) => {

  const {
    auth,
    singleUserRef,
    driverDocsRef,
    driverDocsRefBack,
    verifyIdImageRef
  } = firebase;

  const uid = auth.currentUser.uid;
  
  if (updateData.licenseImage) {
    await uploadBytesResumable(driverDocsRef(uid), updateData.licenseImage);
    updateData.licenseImage = await getDownloadURL(driverDocsRef(uid));
  }
  if (updateData.licenseImageBack) {
    await uploadBytesResumable(driverDocsRefBack(uid),updateData.licenseImageBack);
    updateData.licenseImageBack = await getDownloadURL(driverDocsRefBack(uid));
  }
  if (updateData.verifyIdImage) {
    await uploadBytesResumable(verifyIdImageRef(uid), updateData.verifyIdImage);
    updateData.verifyIdImage = await getDownloadURL(verifyIdImageRef(uid));
  }

  update(singleUserRef(uid), updateData);
};


export const updateProfileImage = (imageBlob) => {

  const {
    auth,
    singleUserRef,
    profileImageRef,
  } = firebase;

  const uid = auth.currentUser.uid;

  uploadBytesResumable( profileImageRef(uid), imageBlob).then(() => {
    imageBlob.close()
    return getDownloadURL(profileImageRef(uid))
  }).then((url) => {
    update(singleUserRef(uid), {
      profile_image: url
    });
  })
};

export const updateWebProfileImage = async (imageBlob) => {

  const {
    auth,
    singleUserRef,
    profileImageRef
  } = firebase;

  const uid = auth.currentUser.uid;

  await uploadBytesResumable(profileImageRef(uid), imageBlob);
  let image = await getDownloadURL(profileImageRef(uid));
  update(singleUserRef(uid), {profile_image: image});

};
export const updateCustomerProfileImage = async (imageBlob, id) => {

  const {
    singleUserRef,
    profileImageRef
  } = firebase;

  const uid = id

  await uploadBytesResumable(profileImageRef(uid), imageBlob);
  let image = await getDownloadURL(profileImageRef(uid));
  update(singleUserRef(uid), {profile_image: image});

};

export const updatePushToken = (token, platform)  => {

  const {
    auth,
    singleUserRef,
  } = firebase;

  if (!singleUserRef || !auth || !auth.currentUser) {
    console.warn('Database not available, cannot update push token');
    return;
  }

  const uid = auth.currentUser.uid;

  update(singleUserRef(uid), {
    pushToken: token,
    userPlatform: platform
  });
};

export const clearLoginError = () => (dispatch) => {
  dispatch({
    type: CLEAR_LOGIN_ERROR,
    payload: null
  });
};

export const fetchWalletHistory = () => (dispatch) => {
  const {
    auth,
    walletHistoryRef
  } = firebase;

  if (!walletHistoryRef || !auth || !auth.currentUser) {
    console.warn('Database not available, cannot fetch wallet history');
    return;
  }

  const uid = auth.currentUser.uid;

  onValue(walletHistoryRef(uid) , snapshot => {
    const data = snapshot.val(); 
    if(data){
      const arr = Object.keys(data).map(i => {
        data[i].id = i
        return data[i]
      });
      dispatch({
        type: UPDATE_USER_WALLET_HISTORY,
        payload: arr.reverse()
      });
    }
  });
};
export const fetchUserWalletHistory = (userId) => (dispatch) => {
  const {
    auth,
    walletHistoryRef
  } = firebase;

  const uid = userId;

  onValue(walletHistoryRef(uid) , snapshot => {
    const data = snapshot.val(); 
    if(data){
      const arr = Object.keys(data).map(i => {
        data[i].id = i
        return data[i]
      });
      dispatch({
        type: UPDATE_USER_WALLET_HISTORY,
        payload: arr.reverse()
      });
    }else{
      dispatch({
        type: UPDATE_USER_WALLET_HISTORY,
        payload:[]
      });
    }
  });
};

export const sendResetMail = (email) => async (dispatch) => {
  const {
    authRef
  } = firebase;

  dispatch({
    type: SEND_RESET_EMAIL,
    payload: email
  });
  sendPasswordResetEmail(authRef(), (email || '').trim()).then(function() {
    console.log('sendResetMail: reset email request accepted by Firebase for', (email || '').trim());
    dispatch({
      type: SEND_RESET_EMAIL_SUCCESS,
      payload: email
    });
  }).catch(function (error) {
      console.warn('sendResetMail FAILED:', error && error.code, error && error.message);
      const lang = store.getState().languagedata.defaultLanguage || {};
      dispatch({
        type: SEND_RESET_EMAIL_FAILED,
        payload: {
          code: error.code || lang.auth_error,
          message: error.code === 'auth/user-not-found'
            ? (lang.not_registred || lang.auth_error)
            : (error.message || lang.not_registred)
        }
      });
  });
};

export const verifyEmailPassword = (email, pass) => async (dispatch) => {
  const {
    auth
  } = firebase;

  console.log('verifyEmailPassword: Attempting login with email:', email);
  
  dispatch({
    type: USER_SIGN_IN,
    payload: null
  });

  signInWithEmailAndPassword(auth, email, pass)
    .then((user) => {
      console.log('verifyEmailPassword: Login successful', user.user.uid);
      //OnAuthStateChange takes care of Navigation
    })
    .catch((error) => {
      console.log('verifyEmailPassword: Login failed', error.code, error.message);
      // Passer l'objet d'erreur complet pour permettre un affichage détaillé
      dispatch({
        type: USER_SIGN_IN_FAILED,
        payload: {
          code: error.code,
          message: error.message
        }
      });
    });
}

export const requestMobileOtp = (mobile) => async (dispatch) => {
  const {
    config
  } = firebase;
  dispatch({
    type: REQUEST_OTP,
    payload: true
  }); 

  const settings = store.getState().settingsdata.settings;
  let host = window && window.location && settings.CompanyWebsite === window.location.origin? window.location.origin : `https://${config.projectId}.web.app`
  let url = `${host}/request_mobile_otp`;
  try{
    console.log('requestMobileOtp: POST', url, 'body=', { mobile });

    // Try primary host (hosting rewrite) then fallback to cloudfunctions.net on network errors
    const primaryUrl = url;
    // derive region from config.databaseURL if available (e.g. '...europe-west1...')
    let region = 'us-central1';
    try {
      if (config && config.databaseURL) {
        const m = String(config.databaseURL).match(/\.([a-z0-9-]+)\.firebasedatabase/);
        if (m && m[1]) region = m[1];
      }
    } catch (e) {
      console.log('requestMobileOtp: failed to derive region from databaseURL', e && e.message);
    }
    const fallbackUrl = `https://${region}-${config.projectId}.cloudfunctions.net/request_mobile_otp`;

    const doFetch = async (targetUrl) => {
      try {
        const r = await fetch(targetUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ mobile: mobile }),
        });
        return r;
      } catch (e) {
        // Network-level error (TypeError: Network request failed)
        console.log('requestMobileOtp: fetch network error for', targetUrl, e && e.message ? e.message : e);
        throw e;
      }
    };

    let response;
    try {
      response = await doFetch(primaryUrl);
    } catch (primaryErr) {
      console.warn('requestMobileOtp: primary host failed, attempting fallback:', fallbackUrl);
      try {
        response = await doFetch(fallbackUrl);
      } catch (fallbackErr) {
        console.error('requestMobileOtp: both primary and fallback failed', { primaryErr: String(primaryErr), fallbackErr: String(fallbackErr) });
        throw fallbackErr || primaryErr;
      }
    }

    const contentType = response.headers && response.headers.get ? (response.headers.get('content-type') || '') : '';
    // If the response is JSON parse it, otherwise read text and surface it for debugging
    if (contentType.indexOf('application/json') !== -1) {
      const result = await response.json();
      if (result && result.success) {
        dispatch({
          type: REQUEST_OTP_SUCCESS,
          payload: true
        });
      } else {
        const err = result && result.error ? result.error : 'Unknown error';
        console.log('requestMobileOtp: server returned error JSON:', result);
        dispatch({
          type: REQUEST_OTP_FAILED,
          payload: err
        });
      }
    } else {
      // Non-JSON response (HTML, plain text, error message). Read as text for diagnosis.
      const text = await response.text();
      console.log('requestMobileOtp: non-JSON response from server. status=', response.status, 'body=', text);
      dispatch({
        type: REQUEST_OTP_FAILED,
        payload: `Non-JSON response from server (status ${response.status})` + (text ? `: ${text}` : '')
      });
    }
  }catch(error){
    console.log('requestMobileOtp: fetch error', error);
    dispatch({
      type: REQUEST_OTP_FAILED,
      payload: error && error.message ? error.message : String(error)
    });
  }
}

export const verifyMobileOtp = (mobile, otp) => async (dispatch) => {
  const {
    auth,
    config
  } = firebase;
  const body = {
    mobile: mobile,
    otp: otp
  };
  try{
    const settings = store.getState().settingsdata.settings;
    let host = window && window.location && settings.CompanyWebsite === window.location.origin? window.location.origin : `https://${config.projectId}.web.app`
    let url = `${host}/verify_mobile_otp`;
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body)
    })
    const result = await response.json();
    if(result.token){
      signInWithCustomToken(auth,result.token)
        .then((user) => {
          //OnAuthStateChange takes care of Navigation
        })
        .catch((error) => {
          dispatch({
            type: USER_SIGN_IN_FAILED,
            payload: error
          });
        });
    }else{
      dispatch({
        type: USER_SIGN_IN_FAILED,
        payload: result.error
      });
    }
  }catch(error){
    console.log(error);
    dispatch({
      type: USER_SIGN_IN_FAILED,
      payload: error
    });
  }
}

export const updateAuthMobile = async ( mobile, otp) => {
  const {
    auth,
    config
  } = firebase;

  const uid = auth.currentUser.uid;
  const body = {
    uid: uid,
    mobile: mobile,
    otp: otp
  };

  const settings = store.getState().settingsdata.settings;
  let host = window && window.location && settings.CompanyWebsite === window.location.origin ? window.location.origin : `https://${config.projectId}.web.app`
  let url = `${host}/update_auth_mobile`;
  try{
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body)
    })
    const result = await response.json();
    if(result.success){
      return {success: true}
    }else{
      return {success: false, error: result.error}
    }
  }catch(error){
    return {success: false, error: error}
  }
}

/**
 * Upload a driver document to Firebase Storage and update user profile
 * Gère tous les types de documents: VTC, K-bis, assurance, RC PRO, carte grise, etc.
 * @param {string} uid - User ID
 * @param {Blob} imageBlob - Document image blob
 * @param {string} documentType - Type de document (vtcCard, kbis, vehicleInsurance, rcPro, vehicleRegistration, etc.)
 * @param {Object} metadata - Optional metadata (expirationDate, status, etc.)
 */
export const updateDriverDocument = async (uid, imageBlob, documentType, metadata = {}) => {
  const {
    auth,
    singleUserRef,
    idImageRef,
    idImageBackRef,
    vtcCardRef,
    vtcCardBackRef,
    vtcStickerRef,
    vtcRegistryRef,
    medicalCertificateRef,
    kbisRef,
    vehicleInsuranceRef,
    rcProRef,
    eatRcProRef,
    vehicleRegistrationRef,
    technicalInspectionRef
  } = firebase;

  // Mapping des types de documents vers leurs refs Firebase
  const documentRefs = {
    idImage: idImageRef,
    idImageBack: idImageBackRef,
    vtcCard: vtcCardRef,
    vtcCardBack: vtcCardBackRef,
    vtcSticker: vtcStickerRef,
    vtcRegistry: vtcRegistryRef,
    medicalCertificate: medicalCertificateRef,
    kbis: kbisRef,
    vehicleInsurance: vehicleInsuranceRef,
    rcPro: rcProRef,
    eatRcPro: eatRcProRef,
    vehicleRegistration: vehicleRegistrationRef,
    technicalInspection: technicalInspectionRef
  };

  const storageRef = documentRefs[documentType];
  if (!storageRef) {
    throw new Error(`Unknown document type: ${documentType}`);
  }

  try {
    // Upload vers Firebase Storage
    await uploadBytesResumable(storageRef(uid), imageBlob);
    const downloadURL = await getDownloadURL(storageRef(uid));

    // Préparer les données de mise à jour
    const updateData = {
      [documentType]: downloadURL
    };

    // Ajouter les métadonnées si présentes
    if (metadata.expirationDate || metadata.status) {
      updateData[`documents/${documentType}`] = {
        url: downloadURL,
        uploadedAt: new Date().toISOString(),
        status: metadata.status || 'pending',
        ...(metadata.expirationDate && { expirationDate: metadata.expirationDate }),
        ...(metadata.notes && { notes: metadata.notes })
      };
    }

    // Mettre à jour le profil utilisateur
    await update(singleUserRef(uid), updateData);

    return { success: true, url: downloadURL };
  } catch (error) {
    console.error('updateDriverDocument error:', error);
    throw error;
  }
};
